import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { FilePlus } from 'lucide-react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import logException from '@dashboard/services/logException';
import usePage from '@dashboard/hooks/usePage';

import FileDownloadsList from './FileDownloadsList';

const PageFilesForm = () => {
  const { page, updatePage, attachFile, detachFile, refetch: refetchPage } = usePage();
  const { uploadFile, deleteFile } = useFileQueries();
  const { sellerId } = useAuth();

  // In-flight uploads that don't exist on the server yet.
  const [uploads, setUploads] = useState([]);

  // Populated file objects we've seen, so a reorder that briefly leaves the
  // cache holding only file ids can still be rendered with full details.
  const knownFilesById = useRef(new Map());
  (page.files || []).forEach(file => {
    if (file && typeof file === 'object' && file._id) {
      knownFilesById.current.set(file._id, file);
    }
  });

  const serverFiles = (page.files || [])
    .map(file => (typeof file === 'string' ? knownFilesById.current.get(file) : file))
    .filter(Boolean);

  const serverFileIds = new Set(serverFiles.map(file => file._id));

  // Keep uploads visible — in-flight ones, and finished ones the server's files
  // array hasn't reflected yet — until the page query catches up and dedupes them.
  const pendingUploads = uploads.filter(upload => !serverFileIds.has(upload._id));

  const files = [...serverFiles, ...pendingUploads];

  const onDrop = useCallback(
    async acceptedFiles => {
      const newUploads = acceptedFiles.map(file =>
        Object.assign(file, {
          key: uniqueId(),
          preview: URL.createObjectURL(file),
          isUploading: true,
          isUploaded: false,
          uploadProgress: 0,
        }),
      );

      setUploads(prev => [...prev, ...newUploads]);

      async function upload(file) {
        try {
          const { data, error } = await uploadFile({
            file,
            bucket: 'files',
            sellerId,
            onProgress: progress => {
              setUploads(prev =>
                prev.map(item =>
                  item === file ? Object.assign(file, { uploadProgress: progress }) : item,
                ),
              );
            },
          });

          if (error) {
            throw error;
          }

          await attachFile.mutateAsync(data._id);

          // Replace the placeholder with the saved file. It stays visible (and
          // resolvable through a reorder) until the page's files array reflects it.
          knownFilesById.current.set(data._id, data);
          setUploads(prev => prev.map(item => (item === file ? { ...data } : item)));
        } catch (error) {
          logException({ message: 'Error in uploading download', error });
          setUploads(prev => prev.filter(item => item !== file));
        }
      }

      await Promise.all(newUploads.map(upload));
    },
    [uploadFile, attachFile, sellerId],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  function onEditFile() {
    refetchPage();
  }

  async function onDeleteFile(file) {
    if (file._id) {
      await detachFile.mutateAsync(file._id);
      await deleteFile(file._id);
      setUploads(prev => prev.filter(item => item._id !== file._id));
      return;
    }

    setUploads(prev => prev.filter(item => item !== file));
  }

  function onReorderFiles(reorderedFiles) {
    updatePage.mutateAsync({ files: reorderedFiles.map(file => file._id).filter(Boolean) });
  }

  return (
    <div>
      <FileDownloadsList
        files={files}
        onDeleteFile={onDeleteFile}
        onEditFile={onEditFile}
        onReorderFiles={onReorderFiles}
        deleteDescription="Past leads won't be able to download this file anymore."
      />

      <div
        {...getRootProps()}
        className={clsx(
          'transition-colors p-4 border border-dashed border-input rounded-md cursor-pointer bg-background hover:bg-accent/40 hover:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isDragActive && 'border-solid border-ring bg-accent/40',
        )}
      >
        <input {...getInputProps()} />

        <div className="flex items-center justify-start gap-3">
          <div className="flex items-center justify-center h-9 w-9 rounded-md bg-muted text-muted-foreground shrink-0 @max-[14rem]/upload:hidden">
            <FilePlus aria-hidden="true" strokeWidth={1.75} className="h-4 w-4" />
          </div>

          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Upload files</span> or drag and drop
          </div>
        </div>

        <FileContainer isDragActive={isDragActive} />
      </div>
    </div>
  );
};

PageFilesForm.propTypes = {
  page: PropTypes.object,
};

function FileContainer({ isDragActive }) {
  if (isDragActive) {
    return <p></p>;
  }

  return <p></p>;
}

FileContainer.propTypes = {
  isDragActive: PropTypes.bool,
};

export default PageFilesForm;
